import './styles.scss'
import React, { useEffect, useRef, useState } from 'react'
import { GoogleMap, LoadScript, OverlayView } from '@react-google-maps/api'
import { Dropdown } from 'primereact/dropdown'
import { DataTable } from 'primereact/datatable'
import { SelectButton } from 'primereact/selectbutton'
import { Column } from 'primereact/column'
import { Chart } from 'primereact/chart'
import { CSVLink } from 'react-csv'
import { useTranslation } from 'react-i18next'
import { ROUTE_STRINGS, TABLE_CONST } from 'Utils/Constants'
import apiAdapterCoffeeWeb from 'Services/apiAdapterCoffeeWeb'
import LayoutContainer from 'Components/LayoutComponent'
import { ButtonComponent, DialogModal } from 'Components/UIComponents'
import Loader from 'Components/Loader'
import { commonMethods } from 'Utils/commonMethods'
import { Dialog } from 'primereact/dialog'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { getLocalUserDetails } from 'Utils/LocalStorageHandler'

const UserStats = ({ setLoading, mockSubscriptionList, mockFileName, mockTableSelectedValue }) => {
  const { t } = useTranslation()
  const history = useHistory()

  const mapRef = useRef(null)
  const popupRef = useRef(null)
  const userDetails = getLocalUserDetails()

  let rowCount = 7
  let paginationButtons = TABLE_CONST.paginationButtonCount
  const staticColorsForDifferentDevices = ['#50b3f6', 'red', 'blue', 'gray']
  const selectOptions = ['Subscription', 'Device']
  const center = { lat: 20, lng: 0 }
  const containerStyle = {
    width: '100%',
    height: '100%'
  }

  const KNOWN_SUBSCRIPTION_COLORS = {
    regular: '#50b3f6',
    gold: '#a48011',
    platinum: '#8a8a8a',
    trialplatinum: '#8a8a8a'
  }

  const COLOR_POOL = ['#FF8A65', '#81C784', '#F06292'] // Color Options for any newly added chart data if not known

  const [chartData, setChartData] = useState({})
  const [chartOptions, setChartOptions] = useState({})
  const [subscriptionList, setSubscriptionList] = useState([])
  const [selectedSubscription, setSelectedSubscription] = useState(null)
  const [totalUsersCount, setTotalUsersCount] = useState(null)
  const [userList, setUserList] = useState([])
  const [filteredUserList, setFilteredUserList] = useState([])
  const [windowSize, setWindowSize] = useState(window.innerWidth)
  const [stackedBarData, setStackedBarData] = useState({})
  const [stackedBarOptions, setStackedBarOptions] = useState({})
  const [fileName, setFileName] = useState('')
  const [showTableMessage, setShowTableMessage] = useState(false)
  const [showDialog, setShowDialog] = useState({ showModal: false, modalType: '', message: '', modalCancel: '', onHide: () => {} })
  const [chartOptionsMap, setChartOptionsMap] = useState({})
  // const [dataLevel, setDataLevel] = useState('country') Commented For Future Ref for State wise data
  const [mapVisibility, setMapVisibility] = useState(false)
  const [viewMode, setViewMode] = useState('Subscription')
  const [value, setValue] = useState(selectOptions[0])
  const [centerState, setCenterState] = useState(center)
  const [clickedLocation, setClickedLocation] = useState(null)
  const [zoom, setZoom] = useState(3)
  const tableSelectOptions = ['Country', 'Subscription']
  const [tableSelectedValue, setTableSelectedValue] = useState(tableSelectOptions[0])
  const [filteredCountryWiseData, setFilteredCountryWiseData] = useState([])
  const [countryBasedMapData, setCountryBasedMapData] = useState([])
  const [deviceOptionList, setDeviceOptionList] = useState([])
  const [selectedDevice, setSelectedDevice] = useState(null)
  const columns = [
    { field: selectedSubscription?.subscriptionId === 0 ? 'serialNumber' : 'subUniqueNumber', header: `${t('SL')}#` },
    { field: 'userName', header: t('USER_NAME') },
    { field: 'country', header: t('COUNTRY') },
    { field: 'subscriptionName', header: t('SUBSCRIPTION_NAME') },
    { field: 'occupation', header: t('OCCUPATION') },
    { field: 'deviceUsed', header: t('DEVICE_USED') },
    { field: 'customCreatedOn', header: t('CREATED_ON') },
    { field: 'userRegistered', header: t('USER_REGISTERED') },
    { field: 'customSubEndDate', header: t('SUBSCRIPTION_EXPIRY_DATE') }
  ]

  const countryBasedSubscriptionColumns = [
    { field: 'srNo', header: `${t('SL')}#` },
    { field: 'country', header: t('COUNTRY'), className: 'country-column' },
    { field: 'users', header: t('USER_COUNT') },
    { field: 'regular', header: t('REGULAR_SUBSCRIPTION') },
    { field: 'gold', header: t('GOLD_SUBSCRIPTION') },
    { field: 'platinum', header: t('PLATINUM_SUBSCRIPTION') },
    { field: 'trialUserCount', header: t('TRIAL_PLATINUM') }
  ]

  const options = {
    plugins: {
      legend: {
        labels: {
          usePointStyle: true
        }
      }
    }
  }

  const optionsMap = {
    plugins: {
      legend: {
        display: false,
        labels: {
          usePointStyle: true
        }
      }
    },
    responsive: true,
    maintainAspectRatio: false
  }

  useEffect(() => {
    if (!userDetails?.isDashboardAvailable) {
      history.push(ROUTE_STRINGS.coffeequotes)
    }

    getUserStats()
    setChartOptions(options)
    setChartOptionsMap(optionsMap)

    // Testing Code
    if (mockSubscriptionList) {
      setSubscriptionList(mockSubscriptionList)
      setFileName(mockFileName)
      setTableSelectedValue(mockTableSelectedValue)
    }

    window.addEventListener('resize', handleWindowResize)

    return () => {
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [])

  useEffect(() => {
    if (selectedSubscription) {
      if (selectedSubscription.name === 'All') {
        setFileName(`${t('USER_STATS_REPORT')}_${getCurrentDate()}`)
      } else {
        const subscriptionName = selectedSubscription.name

        setFileName(`${subscriptionName}_${t('STATS_REPORT')}_${getCurrentDate()}`)
      }
    }
  }, [selectedSubscription])

  if (windowSize < TABLE_CONST.maxWidthTab) {
    rowCount = 8
    paginationButtons = TABLE_CONST.mobilePaginationButtonCount
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setClickedLocation(null)
      }
    }

    // Delay event listener attachment until popupRef is ready
    if (clickedLocation) {
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside)
      }, 0)
    }

    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [clickedLocation])

  const handleZoomChanged = () => {
    if (mapRef.current) {
      const newZoom = mapRef.current.getZoom()

      setZoom(newZoom)

      // Commented For Future Ref for State wise data
      // if (newZoom > 5) {
      //   setDataLevel('city')
      // } else {
      //   setDataLevel('country')
      // }
    }
  }

  // Handle View on Maps Button Click
  const handleMapClick = () => {
    setMapVisibility(true)
    setViewMode('Subscription')
    setValue('Subscription')
    resetDropdown()
    // setDataLevel('country') Commented For Future Ref for State wise data
    if (mapRef.current) {
      mapRef.current.setZoom(3) // Default zoom
      mapRef.current.setCenter(center) // Default center
      setCenterState(center)
    }
  }

  //  Handle Map Dashboard close action
  const handleDialogHide = () => {
    // setDataLevel('country') Commented For Future Ref for State wise data
    setMapVisibility(false)
    setClickedLocation(null)
    resetDropdown()
  }

  // Handling Map Dashboard Select Button
  const handleButtonClick = (mode) => {
    if (mode) {
      setValue(mode)
      setViewMode(mode)
      resetDropdown()
    }

    // Reset map to default zoom and center
    if (mapRef.current) {
      mapRef.current.setZoom(3) // Default zoom
      mapRef.current.setCenter(center) // Default center
      setCenterState(center)
    }
  }

  const returnDataSet = (country) => {
    let colorIndex = 0

    // To get any separate color apart from known color as a fallback
    const getNextColorFromPool = () => {
      const color = COLOR_POOL[colorIndex % COLOR_POOL.length]

      colorIndex += 1

      return color
    }

    const dataSet = {
      all: {
        data: [],
        backgroundColor: []
      }
    }

    const isSubscription = viewMode === 'Subscription'
    const selected = isSubscription ? selectedSubscription : selectedDevice
    const selectedKey = selected?.name?.toLowerCase()
    const KNOWN_DEVICE_COLORS = {}

    deviceOptionList.slice(1).forEach((el, index) => {
      KNOWN_DEVICE_COLORS[el.device.toLowerCase()] = staticColorsForDifferentDevices[index]
    })
    const listData = isSubscription ? subscriptionList : deviceOptionList
    const COLOR_OPTIONS = isSubscription ? KNOWN_SUBSCRIPTION_COLORS : KNOWN_DEVICE_COLORS

    listData.forEach(({ name }) => {
      const key = name.toLowerCase()
      const value = country[key]

      if (value != null) {
        const color = COLOR_OPTIONS[key] || getNextColorFromPool()

        // To add data to specific selected data
        dataSet[key] = {
          data: [value],
          backgroundColor: [color]
        }

        // To add data to all
        dataSet.all.data.push(value)
        dataSet.all.backgroundColor.push(color)
      }
    })

    return dataSet[selectedKey] || dataSet.all
  }

  const getPieData = (country) => {
    const dataset = returnDataSet(country)

    return dataset ? { datasets: [dataset] } : {}
  }

  //   Get user stats along with subscription detail
  const getUserStats = async () => {
    setLoading(true)
    setShowTableMessage(true)
    const fetchAllUserListReport = apiAdapterCoffeeWeb.getAllUserListReport() // Populates Subscription base Table and Dropdowns in the Page
    const fetchAllUserListCountReport = apiAdapterCoffeeWeb.getAllUserListCountReport() // API to Populate the map and Country Based Table Data

    try {
      const [
        {
          data: { returnLst: allUserListResponse }
        },
        {
          data: { returnLst: allUserListCountResponse }
        }
      ] = await Promise.all([fetchAllUserListReport, fetchAllUserListCountReport])

      const { subscription, totalUserCount, userlist, userDevices } = allUserListResponse
      const modifiedSubscriptionList = subscription.map((ele) => ({ ...ele, name: ele.subscriptionName }))
      const modifiedDeviceList = userDevices.map((el) => ({ ...el, name: el?.device }))

      const countryWiseSubscriptionData = allUserListCountResponse.flatMap((el) => el.subscriptions)
      const modifiedCountryData = transformToMapData(allUserListCountResponse, modifiedDeviceList, modifiedSubscriptionList) // Transforming raw data into Map relevant country based data

      setFilteredCountryWiseData(countryWiseSubscriptionData)
      setUserList(userlist)
      setFilteredUserList(userlist)
      setCountryBasedMapData(modifiedCountryData)

      const allDropDownObject = [
        {
          name: 'All',
          subscriptionName: 'All',
          subscriptionColor: '',
          subscriptionUserCount: totalUserCount,
          subscriptionId: 0
        }
      ]

      const allDeviceOptions = [
        {
          name: 'All',
          deviceName: 'All'
        }
      ]

      setSubscriptionList([...allDropDownObject, ...modifiedSubscriptionList]) // Dropdown Options for Subscription
      setDeviceOptionList([...allDeviceOptions, ...modifiedDeviceList]) // Dropdown Options for Device Types
      getChartInfo(modifiedSubscriptionList)
      setSelectedSubscription(allDropDownObject[0]) // Initial Dropdown Value
      setSelectedDevice(allDeviceOptions[0]) // Initial Dropdown Value
      setTotalUsersCount(totalUserCount)

      getSubscriptionStatsDetails(subscription)
    } catch (err) {
      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: 'NO_DATA_FOUND',
        modalCancel: false,
        onHide: commonMethods.handleRetry
      })
    } finally {
      setLoading(false)
      setShowTableMessage(false)
    }
  }

  // Transform raw data into a format suitable for rendering on the map (device + subscription counts per country)
  const transformToMapData = (rawData, modifiedDeviceList, modifiedSubscriptionList) =>
    rawData.map((country) => {
      // On each country iteration subscription and device count data is being created

      const deviceCounts = {}
      const subscriptionCounts = {}

      // Creating Subscription Count Data
      modifiedSubscriptionList.forEach((subscriptionData) => {
        const subscriptionKey = subscriptionData?.name?.toLowerCase()

        subscriptionCounts[subscriptionKey] = 0

        subscriptionCounts[subscriptionKey] = country.subscriptions[0][subscriptionKey]
      })

      //  Creating consistent device count object to have all device types
      modifiedDeviceList.forEach((device) => {
        deviceCounts[device?.name?.toLowerCase()] = 0
      })

      // Populating device types with what is received in the response else will remain zero
      country.deviceTypes?.forEach((el) => {
        const device = el?.loginDevice?.toLowerCase()
        const totalCount = (el?.regular_Count || 0) + (el?.gold_Count || 0) + (el?.platinum_Count || 0)

        if (device && Object.prototype.hasOwnProperty.call(deviceCounts, device)) {
          deviceCounts[device] += totalCount
        }
      })

      // returning subscription and device data for each country
      return {
        lat: country.subscriptions[0].latitude || 0,
        lng: country.subscriptions[0].longitude || 0,
        countryName: country?.subscriptions[0].country,
        ...subscriptionCounts,
        ...deviceCounts
      }
    })

  //   Get charts information
  const getChartInfo = (list) => {
    const data = {
      labels: getChartLabels(list),
      datasets: [
        {
          data: getChartCount(list),
          backgroundColor: getChartColors(list)
        }
      ]
    }

    setChartData(data)
  }

  //   Get user counts for chart
  const getChartCount = (list) => {
    const userCounts = list.map(({ subscriptionUserCount }) => subscriptionUserCount)

    return userCounts
  }

  //   Get chart labeling
  const getChartLabels = (list) => {
    const chartLabelings = list.map(({ subscriptionName }) => subscriptionName)

    return chartLabelings
  }

  //   Get chart colors based on subscription
  const getChartColors = (list) => {
    const colorCodes = list.map(({ subscriptionColor }) => {
      if (subscriptionColor) {
        return subscriptionColor.split(',')[0].trim(' ')
      }

      return 'lightgray'
    })

    return colorCodes
  }

  //   Get badge color based on subscription
  const getIndividualColor = (subscriptionColor) => {
    if (subscriptionColor) {
      return {
        color: subscriptionColor.split(',')[0].trim(' ')
      }
    }

    return { color: 'black' }
  }

  // Handle change subscription dropdown
  const handleChangeSubscription = (item) => {
    const { subscriptionId: selectedSubscriptionId } = item
    const filterdUserList = userList?.filter(({ subscriptionId }) => subscriptionId === selectedSubscriptionId)

    setFilteredUserList(selectedSubscriptionId === 0 ? userList : filterdUserList)
    setSelectedSubscription(item)
  }

  const handleDropdownChange = (item) => {
    viewMode === 'Subscription' ? setSelectedSubscription(item) : setSelectedDevice(item)
  }

  const handleWindowResize = () => setWindowSize(window.innerWidth)

  const handleDismiss = () => {
    setShowDialog({ ...showDialog, showModal: false })
  }

  // To view the card of specific country on click
  const handleChartClick = (countryData, event, type) => {
    event.stopPropagation()

    const isSubscription = type === 'Subscription'
    const selected = isSubscription ? selectedSubscription : selectedDevice
    const list = isSubscription ? subscriptionList : deviceOptionList
    const selectedKey = selected?.name?.toLowerCase()
    const dataSet = { all: {} }

    list.forEach(({ name }) => {
      const key = name.toLowerCase()
      const value = countryData[key]

      if (value != null) {
        dataSet[key] = value
        dataSet.all[key] = value
      }
    })

    const chartData = selectedKey === 'all' ? dataSet[selectedKey] : { [selectedKey]: dataSet[selectedKey] }

    setClickedLocation({
      countryName: countryData.countryName,
      lat: countryData.lat,
      lng: countryData.lng,
      type,
      ...chartData
    })
  }

  // Select Button Functionality for Table
  const handleTableSelectButtonClick = (selectedValue) => {
    if (selectedValue) {
      setTableSelectedValue(selectedValue)
    }
  }

  const getStats = (list) => {
    const arrayList = []

    const colors = staticColorsForDifferentDevices
    const allSubscription = list?.forEach((ele) => {
      ele.userDevices.forEach((ele) => {
        arrayList.push(ele)
      })
    })

    const newArray = []

    const statsArray = arrayList?.forEach(({ device, usersCount }) => {
      const isPresent = newArray?.some(({ label }) => label === device)

      if (!isPresent) {
        const object = {
          type: 'bar',
          label: device,
          backgroundColor: colors[newArray?.length],
          data: [usersCount]
        }

        newArray.push(object)
      } else {
        const foundObject = newArray.find((item) => item.label === device)

        foundObject.data.push(usersCount)
      }
    })

    return newArray
  }

  const getSubscriptionStatsDetails = (list) => {
    const documentStyle = getComputedStyle(document.documentElement)
    const textColor = documentStyle.getPropertyValue('--text-color')
    const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary')
    const surfaceBorder = documentStyle.getPropertyValue('--surface-border')

    const data = {
      labels: list.map(({ subscriptionName }) => subscriptionName),
      datasets: getStats(list)
    }
    const options = {
      maintainAspectRatio: false,
      aspectRatio: 0.8,
      plugins: {
        tooltips: {
          mode: 'index',
          intersect: false
        },
        legend: {
          labels: {
            color: textColor
          }
        }
      },
      scales: {
        x: {
          stacked: true,
          ticks: {
            color: textColorSecondary
          },
          grid: {
            color: surfaceBorder
          }
        },
        y: {
          stacked: true,
          ticks: {
            color: textColorSecondary
          },
          grid: {
            color: surfaceBorder
          }
        }
      }
    }

    setStackedBarData(data)
    setStackedBarOptions(options)
  }

  const getCurrentDate = () => {
    const date = new Date()
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const day = date.getDate().toString().padStart(2, '0')
    const year = date.getFullYear()

    return `${month}-${day}-${year}`
  }

  //  To reset the dropdown value to first option i.e. All
  const resetDropdown = () => {
    setSelectedSubscription(subscriptionList[0])
    setSelectedDevice(deviceOptionList[0])
  }

  const tableResponseMessage = () => <div>{showTableMessage ? t('FETCHING') : t('NO_RESULTS_FOUND')}</div>

  const changeCase = (str) => {
    if (str === 'iphone') return 'iPhone'
    if (str === 'ios') return 'iOS'

    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  const MapSelectButtonTemplate = () => (
    <div className="map-buttons-container">
      <SelectButton value={value} onChange={(e) => handleButtonClick(e.value)} options={selectOptions} />
      <Dropdown value={viewMode === 'Subscription' ? selectedSubscription : selectedDevice} className="subscription-dropdown" onChange={(e) => handleDropdownChange(e.value)} options={viewMode === 'Subscription' ? subscriptionList : deviceOptionList} optionLabel="name" placeholder={t('SELECT_SUBSCRIPTION')} />
    </div>
  )

  return (
    <div>
      <LayoutContainer title={'USER_STATS'} breadCrumbsTitle={'userStats'} showMapsButton={true} handleMapClick={handleMapClick}>
        <div className="user-stats-wrapper" data-testid="user-stats">
          <div className="user-stats-contrainer">
            <div className="cards pie-chart-user-detail">
              <div className="pie-chart">
                <Chart type="pie" data={chartData} options={chartOptions} data-testid="pie-chart" />
              </div>
              <div className="sub-counts">
                <div className="total-count">
                  <div className="count" data-testid="total-user-count">
                    {totalUsersCount}
                  </div>
                  <div className="labeling">{t('TOTAL_USERS')}</div>
                </div>
                <div className="user-details" data-testid="user-details-wrapper">
                  {subscriptionList?.map(({ subscriptionName, subscriptionColor, subscriptionUserCount, subscriptionId }, index) => (
                    <>
                      {subscriptionId !== 0 && (
                        <div key={index} className="each-subscription">
                          <div className="count" style={getIndividualColor(subscriptionColor)}>
                            {subscriptionUserCount}
                          </div>
                          <div className="labeling">{subscriptionName}</div>
                        </div>
                      )}
                    </>
                  ))}
                </div>
              </div>
            </div>
            <div>
              <div className="cards">
                <Chart type="bar" data={stackedBarData} options={stackedBarOptions} data-testid="bar-chart" />
              </div>
            </div>
          </div>
          <div className="cards table-data">
            <div className="table-actions">
              <div className="table-select-button">
                <SelectButton value={tableSelectedValue} onChange={(e) => handleTableSelectButtonClick(e.value)} options={tableSelectOptions} data-testid="select-button" />

                {tableSelectedValue === 'Subscription' && <Dropdown value={selectedSubscription} className="subscription-dropdown" onChange={(e) => handleChangeSubscription(e.value)} options={subscriptionList} optionLabel="name" placeholder={t('SELECT_SUBSCRIPTION')} />}
              </div>
              <CSVLink data={tableSelectedValue === 'Subscription' ? filteredUserList.map(({ subscriptionId, ...rest }) => rest) : filteredCountryWiseData} filename={fileName}>
                <ButtonComponent type="custom" label={t('DOWNLOAD_REPORTS')} icon="pi pi-download" variant="action" />
              </CSVLink>
            </div>
            {tableSelectedValue === 'Subscription' ? (
              <DataTable value={filteredUserList} className="card user-stats-table" paginator={filteredUserList.length > 0} pageLinkSize={paginationButtons} rows={rowCount} emptyMessage={tableResponseMessage} scrollable scrollHeight="60vh">
                {columns.map(({ field, header, rowReorder, body }) => (
                  <Column className="table-columns" rowReorder={rowReorder} key={field} field={field} header={header} body={body} />
                ))}
              </DataTable>
            ) : (
              <DataTable value={filteredCountryWiseData} className="card user-stats-table" paginator={filteredCountryWiseData?.length > 0} pageLinkSize={paginationButtons} rows={rowCount} emptyMessage={tableResponseMessage} scrollable scrollHeight="60dvh">
                {countryBasedSubscriptionColumns.map(({ field, header, className, rowReorder, body }) => (
                  <Column className={`table-columns ${className}`} rowReorder={rowReorder} key={field} field={field} header={header} body={body} />
                ))}
              </DataTable>
            )}
          </div>
        </div>
        <DialogModal {...showDialog} onDismiss={() => handleDismiss()} onRetry={commonMethods.handleRetry} />
        {mapVisibility && (
          <Dialog header={<MapSelectButtonTemplate />} className="map-dialog-modal" visible={mapVisibility} onHide={() => handleDialogHide()}>
            <LoadScript googleMapsApiKey={process.env.REACT_APP_MAPS_API_KEY}>
              <GoogleMap
                mapContainerStyle={containerStyle}
                center={centerState}
                zoom={2.4}
                onLoad={(map) => {
                  mapRef.current = map
                }}
                onZoomChanged={handleZoomChanged}
                options={{ maxZoom: 8, zoomControl: true }}
              >
                {countryBasedMapData.map((country, index) => (
                  <OverlayView key={index} position={{ lat: country.lat, lng: country.lng }} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
                    <div onClick={(e) => handleChartClick(country, e, value)}>
                      <Chart
                        type="pie"
                        data={getPieData(country)}
                        options={chartOptionsMap}
                        style={{
                          width: `${zoom * 12}px`,
                          height: `${zoom * 12}px`
                        }}
                      />
                    </div>
                  </OverlayView>
                ))}
                {/* Commented For Future Ref for State wise data */}
                {/* {countries.map((country, index) =>
                  dataLevel === 'country' ? (
                    <OverlayView key={index} position={{ lat: country.lat, lng: country.lng }} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
                      <div style={{ position: 'relative', cursor: 'pointer' }} onClick={(e) => handleChartClick(country, e, value)}>
                        <Chart type="pie" data={getPieData(country)} options={chartOptionsMap} style={{ width: '60px', height: '60px' }} />
                      </div>
                    </OverlayView>
                  ) : (
                    country.cities.map((city, cityIndex) => (
                      <OverlayView key={`city-${cityIndex}`} position={{ lat: city.lat, lng: city.lng }} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
                        <div style={{ position: 'relative', cursor: 'pointer' }} onClick={(e) => handleChartClick(city, e, value)}>
                          <Chart type="pie" data={getPieData(city)} options={chartOptionsMap} style={{ width: '50px', height: '50px' }} />
                        </div>
                      </OverlayView>
                    ))
                  )
                )} */}
                {clickedLocation && (
                  <OverlayView
                    // Commented For Future Ref for State wise data
                    // position={
                    //   dataLevel === 'country' ? { lat: clickedLocation.lat, lng: clickedLocation.lng + 4 } : { lat: clickedLocation.lat + 0.1, lng: clickedLocation.lng + 0.2 } // Smaller offset for cities
                    // }
                    position={{ lat: clickedLocation.lat, lng: clickedLocation.lng }}
                    getPixelPositionOffset={() => ({
                      x: zoom * 12 + 4,
                      y: 0
                    })}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  >
                    <div ref={popupRef} className="location-card">
                      <>
                        <p className="country-name">{clickedLocation?.countryName}</p>
                        {clickedLocation &&
                          Object.entries(clickedLocation).map(([key, value]) => {
                            if (['lat', 'lng', 'type', 'countryName'].includes(key)) return null

                            return (
                              <p key={key}>
                                <strong>{changeCase(key)}:</strong> {value}
                              </p>
                            )
                          })}
                      </>
                    </div>
                  </OverlayView>
                )}
              </GoogleMap>
            </LoadScript>
          </Dialog>
        )}
      </LayoutContainer>
    </div>
  )
}

export default Loader(UserStats)
